import { useState, useEffect, useRef } from 'react';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];
const TIPO_LABELS = { av: 'Avería', in: 'Instalación', mn: 'Mantenimiento' };
const TIPO_COLORS = { av: '#ef4444', in: '#3b82f6', mn: '#8b5cf6' };

function norm(s) { return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim(); }

// Endesa identifica cada instalación por su dirección (calle del centro/subestación).
// Los demás clientes traen la calle real en "dirección" pero el nombre que identifica
// la instalación (p. ej. el cliente final) viene en la columna "usuario" del Excel,
// que se guarda en usuario_responsable.
function instNombre(inst) {
  const esEndesa = (inst.cliente || '').toUpperCase().includes('ENDESA');
  if (esEndesa) return inst.direccion || inst.descripcion || '';
  return inst.usuario_responsable || inst.direccion || inst.descripcion || '';
}

async function downloadDoc(doc) {
  try {
    const res = await api.get(`/api/documents/${doc.id}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(res);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.file_name || `parte-${doc.id}.html`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    alert('Archivo no disponible');
  }
}

export default function Partes() {
  const user = useAuthStore(s => s.user);
  if (!user) return null;
  return ROLES_ADMIN.includes(user.rol) ? <PartesAdmin /> : <PartesTecnico />;
}

// ── Vista admin / jefe / administrativo ──────────────────────────────────────

function PartesAdmin() {
  const [employees,    setEmployees]    = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [empQuery,     setEmpQuery]     = useState('');
  const [empOpen,      setEmpOpen]      = useState(false);
  const [docs,         setDocs]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [editDoc,      setEditDoc]      = useState(null);
  const [search,       setSearch]       = useState('');
  const [repairing,    setRepairing]    = useState(false);
  const [repairMsg,    setRepairMsg]    = useState('');
  const [iframeReady,  setIframeReady]  = useState(false);
  const empRef = useRef(null);
  const repairIframeRef = useRef(null);

  function regenOne(html) {
    return new Promise(resolve => {
      const win = repairIframeRef.current?.contentWindow;
      if (!win) return resolve(null);
      const reqId = Math.random().toString(36).slice(2);
      const timeout = setTimeout(() => { window.removeEventListener('message', onMsg); resolve(null); }, 20000);
      function onMsg(e) {
        if (!e.data || e.data.type !== 'sgv_regen_result' || e.data.reqId !== reqId) return;
        clearTimeout(timeout);
        window.removeEventListener('message', onMsg);
        resolve(e.data.fileB64 || null);
      }
      window.addEventListener('message', onMsg);
      win.postMessage({ type: 'sgv_regen_parte', html, reqId }, '*');
    });
  }

  async function repairHistoricos() {
    if (!window.confirm('Esto regenera como PDF real todos los partes antiguos guardados como HTML. Puede tardar. ¿Continuar?')) return;
    setRepairing(true);
    setRepairMsg('Buscando partes a reparar…');
    try {
      const { data: broken } = await api.get('/api/documents/broken-partes');
      if (!broken || !broken.length) {
        setRepairMsg('No hay partes que reparar.');
        return;
      }
      let ok = 0, fail = 0;
      for (let i = 0; i < broken.length; i++) {
        const d = broken[i];
        setRepairMsg(`Reparando ${i + 1}/${broken.length}…`);
        try {
          const blob = await api.get(`/api/documents/${d.id}/file`, { responseType: 'blob' });
          const html = await blob.text();
          const fileB64 = await regenOne(html);
          if (fileB64) {
            await api.patch(`/api/documents/${d.id}/file`, { file_b64: fileB64 });
            ok++;
          } else fail++;
        } catch { fail++; }
      }
      setRepairMsg(`Listo: ${ok} reparados${fail ? `, ${fail} con error` : ''}.`);
    } catch (e) {
      setRepairMsg('Error al reparar: ' + e.message);
    } finally {
      setRepairing(false);
    }
  }

  useEffect(() => {
    api.get('/api/employees/list').then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const selectedEmp   = employees.find(e => String(e.id) === String(selectedEmpId));
  const filterUserId  = selectedEmp?.user_id || '';

  const empFiltered = empQuery.trim()
    ? employees.filter(e => e.nombre.toLowerCase().includes(empQuery.toLowerCase()))
    : employees;

  function selectEmp(emp) {
    setSelectedEmpId(emp ? String(emp.id) : '');
    setEmpQuery(emp ? emp.nombre : '');
    setEmpOpen(false);
    setSearch('');
  }

  useEffect(() => {
    setLoading(true);
    // Employee has no user account → can't have docs
    if (selectedEmpId && !filterUserId) {
      setDocs([]);
      setLoading(false);
      return;
    }
    const url = filterUserId
      ? `/api/documents/all?user_id=${filterUserId}`
      : '/api/documents/all';
    api.get(url)
      .then(r => setDocs((r.data || []).filter(d => d.tipo === 'parte')))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [selectedEmpId, filterUserId]);

  const filtered = search.trim()
    ? docs.filter(d => {
        const q = search.toLowerCase();
        return (
          (d.parte_data?.fecha     || '').toLowerCase().includes(q) ||
          (d.parte_data?.numero    || '').toLowerCase().includes(q) ||
          (d.nombre                || '').toLowerCase().includes(q) ||
          (d.user_nombre           || '').toLowerCase().includes(q) ||
          new Date(d.created_at).toLocaleDateString('es-ES').includes(q)
        );
      })
    : docs;

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar este parte?')) return;
    try {
      await api.delete(`/api/documents/${id}`);
      setDocs(prev => prev.filter(d => d.id !== id));
    } catch { alert('No se pudo eliminar'); }
  }

  function handleSaved(updatedDoc) {
    setDocs(prev => prev.map(d => d.id === updatedDoc.id ? { ...d, parte_data: updatedDoc.parte_data } : d));
    setEditDoc(null);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Barra filtros */}
      <div style={{ padding: '10px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Empleado</span>
        <div ref={empRef} style={{ position: 'relative', minWidth: 180, maxWidth: 240 }}>
          <input
            type="text"
            value={empQuery}
            placeholder="Todos los empleados…"
            autoComplete="off"
            onChange={e => { setEmpQuery(e.target.value); setEmpOpen(true); if (!e.target.value) selectEmp(null); }}
            onFocus={() => setEmpOpen(true)}
            onBlur={() => setTimeout(() => setEmpOpen(false), 180)}
            onKeyDown={e => {
              if (e.key === 'Escape') { setEmpOpen(false); }
              if (e.key === 'Enter' && empFiltered.length === 1) selectEmp(empFiltered[0]);
            }}
            style={{ background: 'var(--surface-deep)', border: `1.5px solid ${selectedEmpId ? 'var(--amber)' : 'var(--border)'}`, borderRadius: 6, color: 'var(--text)', padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', width: '100%' }}
          />
          {empOpen && empFiltered.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#10131e', border: '1.5px solid var(--amber)', borderTop: 'none', borderRadius: '0 0 8px 8px', maxHeight: 220, overflowY: 'auto', zIndex: 200 }}>
              <div
                onMouseDown={() => selectEmp(null)}
                style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-dim)', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background='var(--surface)'}
                onMouseLeave={e => e.currentTarget.style.background=''}
              >— Todos —</div>
              {empFiltered.map(e => (
                <div
                  key={e.id}
                  onMouseDown={() => selectEmp(e)}
                  style={{ padding: '7px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--border)', color: 'var(--text)' }}
                  onMouseEnter={ev => ev.currentTarget.style.background='var(--surface)'}
                  onMouseLeave={ev => ev.currentTarget.style.background=''}
                >{e.nombre}</div>
              ))}
            </div>
          )}
        </div>
        <input
          type="text"
          placeholder="Buscar por fecha, nº inst. o nombre…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', flex: 1, minWidth: 180 }}
        />
        <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
          {filtered.length}{search ? `/${docs.length}` : ''} parte{filtered.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={repairHistoricos}
          disabled={repairing || !iframeReady}
          title="Regenera como PDF real los partes antiguos guardados como HTML"
          style={{ background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text-dim)', padding: '6px 10px', fontSize: 11, fontWeight: 700, cursor: repairing || !iframeReady ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
        >
          {repairing ? '⏳ Reparando…' : '🛠 Reparar históricos'}
        </button>
        {repairMsg && <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{repairMsg}</span>}
        <iframe
          ref={repairIframeRef}
          src="/partes.html"
          onLoad={() => setIframeReady(true)}
          title="repair-worker"
          style={{ position: 'fixed', left: -9999, top: 0, width: 900, height: 1200, border: 'none', opacity: 0, pointerEvents: 'none' }}
        />
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ padding: 40, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>
        ) : !filtered.length ? (
          <div style={{ padding: 40, color: 'var(--text-dim)', textAlign: 'center' }}>
            {search ? 'Sin resultados para esa búsqueda.' : 'Sin partes de trabajo.'}
          </div>
        ) : (
          <div style={{ maxWidth: 860, margin: '0 auto', display: 'grid', gap: 6 }}>
            {filtered.map(d => (
              <ParteRow key={d.id} d={d} showUser onDelete={handleDelete}
                onEdit={d.parte_data ? () => setEditDoc(d) : null} />
            ))}
          </div>
        )}
      </div>

      {editDoc && (
        <ParteEditorModal doc={editDoc} onClose={() => setEditDoc(null)} onSaved={handleSaved} />
      )}
    </div>
  );
}

// ── Vista técnico: iframe + historial ────────────────────────────────────────

function PartesTecnico() {
  const [tab,     setTab]     = useState('iframe');
  const [docs,    setDocs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState('');
  const [editDoc, setEditDoc] = useState(null);
  const iframeRef = useRef(null);

  function handleSaved(updatedDoc) {
    setDocs(prev => prev.map(d => d.id === updatedDoc.id ? { ...d, parte_data: updatedDoc.parte_data } : d));
    setEditDoc(null);
  }

  function sendToPartes(action) {
    iframeRef.current?.contentWindow?.postMessage({ type: 'sgv_cmd', action }, '*');
  }

  useEffect(() => {
    if (tab !== 'historial') return;
    setLoading(true);
    api.get('/api/documents/mine')
      .then(r => setDocs((r.data || []).filter(d => d.tipo === 'parte')))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [tab]);

  const filteredTec = search.trim()
    ? docs.filter(d => {
        const q = search.toLowerCase();
        return (
          (d.parte_data?.fecha  || '').toLowerCase().includes(q) ||
          (d.parte_data?.numero || '').toLowerCase().includes(q) ||
          (d.nombre             || '').toLowerCase().includes(q) ||
          new Date(d.created_at).toLocaleDateString('es-ES').includes(q)
        );
      })
    : docs;

  useEffect(() => {
    // reqId → id del documento ya creado, para poder adjuntarle el PDF cuando
    // termine de generarse (llega en un segundo mensaje, ver partes.html).
    const pending = {};
    function onMsg(e) {
      if (!e.data) return;
      if (e.data.type === 'sgv_doc' && e.data.docType === 'parte') {
        api.post('/api/documents/create', {
          tipo: 'parte',
          nombre: e.data.nombre || null,
          año: e.data.año || null,
          semana: e.data.semana || null,
          total_gastos: 0,
          nota_numero: null,
          file_b64: e.data.fileB64 || null,
          file_name: e.data.fileName || null,
          parte_data: e.data.parteData || null,
        }).then(r => {
          if (e.data.reqId && r?.data?.id) pending[e.data.reqId] = r.data.id;
        }).catch(() => {});
      } else if (e.data.type === 'sgv_doc_file' && e.data.reqId) {
        const docId = pending[e.data.reqId];
        delete pending[e.data.reqId];
        if (docId && e.data.fileB64) {
          api.patch(`/api/documents/${docId}/file`, { file_b64: e.data.fileB64 }).catch(() => {});
        }
      }
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar este parte?')) return;
    try {
      await api.delete(`/api/documents/${id}`);
      setDocs(prev => prev.filter(d => d.id !== id));
    } catch { alert('No se pudo eliminar'); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', gap: 8, padding: '8px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0, alignItems: 'center' }}>
        <select
          value={tab}
          onChange={e => setTab(e.target.value)}
          style={{
            background: 'var(--surface2)', color: 'var(--text)', border: '1.5px solid var(--border)',
            borderRadius: 6, padding: '7px 10px', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <option value="iframe">📋 Parte de Trabajo</option>
          <option value="historial">🗂️ Historial</option>
        </select>
        {tab === 'iframe' && (
          <>
            <button
              onClick={() => sendToPartes('reset')}
              style={{ background: 'var(--surface2)', color: 'var(--text-dim)', border: 'none', padding: '7px 13px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >&#8635; Limpiar</button>
            <button
              onClick={() => sendToPartes('print')}
              style={{ background: '#1d4ed8', color: '#fff', border: 'none', padding: '7px 13px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
            >&#9108; Imprimir / PDF</button>
          </>
        )}
      </div>

      <div style={{ flex: 1, position: 'relative' }}>
        <iframe
          ref={iframeRef}
          src="/partes.html"
          title="Partes de Trabajo"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', display: tab === 'iframe' ? 'block' : 'none' }}
        />
        {tab === 'historial' && (
          <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: 16 }}>
            <div style={{ maxWidth: 860, margin: '0 auto 12px' }}>
              <input
                type="text"
                placeholder="Buscar por fecha, nº inst. o nombre…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '7px 11px', fontSize: 12, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            {loading ? (
              <div style={{ padding: 40, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>
            ) : !filteredTec.length ? (
              <div style={{ padding: 40, color: 'var(--text-dim)', textAlign: 'center' }}>
                {search ? 'Sin resultados para esa búsqueda.' : 'Sin partes generados aún.'}
              </div>
            ) : (
              <div style={{ maxWidth: 860, margin: '0 auto', display: 'grid', gap: 6 }}>
                {filteredTec.map(d => (
                  <ParteRow key={d.id} d={d} onDelete={handleDelete}
                    onEdit={(d.parte_data && !d.parte_data.admin_firma) ? () => setEditDoc(d) : null} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {editDoc && (
        <ParteEditorModal doc={editDoc} onClose={() => setEditDoc(null)} onSaved={handleSaved} />
      )}
    </div>
  );
}

// ── ParteRow ──────────────────────────────────────────────────────────────────

const TIPO_COLOR_MAP = {
  av: { bg: 'rgba(248,113,113,.12)', color: '#f87171', border: 'rgba(248,113,113,.3)' },
  in: { bg: 'rgba(74,222,128,.12)',  color: '#4ade80', border: 'rgba(74,222,128,.3)'  },
  mn: { bg: 'rgba(96,165,250,.12)',  color: '#60a5fa', border: 'rgba(96,165,250,.3)'  },
};

function ParteRow({ d, onDelete, onEdit, showUser = false }) {
  const [open, setOpen] = useState(false);
  const p    = d.parte_data || {};
  const tc   = TIPO_COLOR_MAP[p.tipo] || TIPO_COLOR_MAP.av;
  const tecs = Array.isArray(p.tecs) ? p.tecs.filter(Boolean) : [];
  const mats = Array.isArray(p.mats) ? p.mats.filter(m => m.art) : [];

  return (
    <div style={{ background: 'var(--surface-deep)', border: `1px solid ${open ? 'rgba(96,165,250,.45)' : 'var(--border)'}`, borderRadius: 8, overflow: 'hidden', transition: 'border-color .18s' }}>
      {/* Cabecera */}
      <div onClick={() => setOpen(o => !o)}
        style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 3, letterSpacing: '.04em', background: tc.bg, color: tc.color, border: `1px solid ${tc.border}` }}>
              {TIPO_LABELS[p.tipo] || 'Parte'}
            </span>
            {p.numero && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 3, background: 'rgba(245,158,11,.12)', color: 'var(--amber)', border: '1px solid rgba(245,158,11,.3)' }}>
                Inst. {p.numero}
              </span>
            )}
            {p.admin_firma && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 3, background: 'rgba(74,222,128,.12)', color: '#4ade80', border: '1px solid rgba(74,222,128,.3)' }}>✔ Firmado</span>
            )}
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.nombre || d.nombre || 'Parte de Trabajo'}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {showUser && d.user_nombre && <span style={{ color: '#a78bfa', fontWeight: 700 }}>{d.user_nombre}</span>}
            <span>{new Date(d.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            {p.fecha     && <span>· {p.fecha}</span>}
            {p.provincia && <span>· {p.provincia}</span>}
            {tecs.length > 0 && <span>· {tecs.join(', ')}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {onEdit && (
            <button onClick={e => { e.stopPropagation(); onEdit(); }} title="Ver / editar"
              style={{ background: 'rgba(167,139,250,.12)', border: '1px solid rgba(167,139,250,.4)', color: '#a78bfa', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 700, lineHeight: 1 }}>
              ✎ Ver
            </button>
          )}
          {d.has_file && (
            <button onClick={e => { e.stopPropagation(); downloadDoc(d); }} title="Descargar parte"
              style={{ background: 'rgba(74,222,128,.12)', border: '1px solid rgba(74,222,128,.4)', color: '#4ade80', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>
              ⬇
            </button>
          )}
          {onDelete && (
            <button onClick={e => { e.stopPropagation(); onDelete(d.id); }} title="Eliminar"
              style={{ background: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.4)', color: '#f87171', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>
              🗑
            </button>
          )}
          <span style={{ fontSize: 18, color: open ? '#60a5fa' : 'var(--text-dim)', display: 'inline-block', transition: 'transform .2s, color .2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', lineHeight: 1 }}>›</span>
        </div>
      </div>

      {/* Panel expandido */}
      {open && (
        <div style={{ borderTop: '1px solid var(--border)', background: 'rgba(0,0,0,.18)', padding: '14px 16px', display: 'grid', gap: 12 }}>
          {p.objeto && <PField label="Objeto del trabajo">{p.objeto}</PField>}
          {p.trabajos && <PField label="Trabajos realizados">{p.trabajos}</PField>}
          {mats.length > 0 && (
            <div>
              <div style={PLBL}>Materiales</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 6 }}>
                <thead>
                  <tr>
                    {[['Cant.','center'],['Artículo','left'],['Precio','right']].map(([h, a]) => (
                      <th key={h} style={{ padding: '4px 8px', textAlign: a, color: 'var(--text-dim)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mats.map((m, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                      <td style={{ padding: '5px 8px', textAlign: 'center', color: 'var(--amber)', fontWeight: 700 }}>{m.qty || '—'}</td>
                      <td style={{ padding: '5px 8px', color: 'var(--text)' }}>{m.art}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--text-dim)' }}>{m.precio ? `${parseFloat(m.precio).toFixed(2)} €` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {p.admin_notas && <PField label="Notas administrativas" amber>{p.admin_notas}</PField>}
          {!p.objeto && !p.trabajos && mats.length === 0 && !p.admin_notas && (
            <div style={{ color: 'var(--text-dim)', fontSize: 12, textAlign: 'center' }}>Sin contenido escrito en este parte.</div>
          )}
        </div>
      )}
    </div>
  );
}

function PField({ label, amber, children }) {
  return (
    <div>
      <div style={{ ...PLBL, color: amber ? 'var(--amber)' : '#60a5fa' }}>{label}</div>
      <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginTop: 4 }}>{children}</div>
    </div>
  );
}
const PLBL = { fontSize: 10, fontWeight: 800, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '.06em' };

// ── ParteEditorModal — admin ve, edita y firma el parte ───────────────────────

function ParteEditorModal({ doc, onClose, onSaved }) {
  const user = useAuthStore(s => s.user);
  const isAdmin = ROLES_ADMIN.includes(user?.rol);
  const pd = doc.parte_data || {};
  const [form, setForm] = useState({
    tipo:      pd.tipo      || 'av',
    numero:    pd.numero    || '',
    nombre:    pd.nombre    || '',
    provincia: pd.provincia || '',
    fecha:     pd.fecha     || '',
    objeto:    pd.objeto    || '',
    trabajos:  pd.trabajos  || '',
    tecs:      pd.tecs      || [''],
    mats:      pd.mats      || [],
    tiempos:   pd.tiempos   || [],
    admin_notas: pd.admin_notas || '',
    admin_firma: pd.admin_firma || null,
  });
  const [saving,      setSaving]      = useState(false);
  const [inventory,   setInventory]   = useState([]);
  const [regenReady,  setRegenReady]  = useState(false);
  const [installations, setInstallations] = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [instSuggest,   setInstSuggest]   = useState([]);
  const [tecSuggestIdx, setTecSuggestIdx] = useState(null);
  const [tecSuggest,    setTecSuggest]    = useState([]);
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPos   = useRef({ x: 0, y: 0 });
  const regenIframeRef = useRef(null);

  // Regenera el PDF con los datos editados (best-effort: si falla, el archivo
  // descargable simplemente se queda con la version anterior)
  function regenPdf(parteData) {
    return new Promise(resolve => {
      const win = regenIframeRef.current?.contentWindow;
      if (!win || !regenReady) return resolve(null);
      const reqId = Math.random().toString(36).slice(2);
      const timeout = setTimeout(() => { window.removeEventListener('message', onMsg); resolve(null); }, 20000);
      function onMsg(e) {
        if (!e.data || e.data.type !== 'sgv_regen_result' || e.data.reqId !== reqId) return;
        clearTimeout(timeout);
        window.removeEventListener('message', onMsg);
        resolve(e.data.fileB64 || null);
      }
      window.addEventListener('message', onMsg);
      win.postMessage({ type: 'sgv_regen_from_data', parteData, reqId }, '*');
    });
  }

  // Cargar instalaciones (autorrelleno de provincia) y técnicos activos (autocompletado)
  useEffect(() => {
    api.get('/api/installations/dropdown').then(r => setInstallations(r.data || [])).catch(() => {});
    api.get('/api/employees/autocomplete').then(r => setEmployees((r.data || []).filter(e => e.nombre))).catch(() => {});
  }, []);

  // Cargar inventario y auto-rellenar precios en materiales
  useEffect(() => {
    api.get('/api/inventario/lista')
      .then(r => {
        const inv = r.data || [];
        setInventory(inv);
        setForm(p => ({
          ...p,
          mats: p.mats.map(m => {
            if (parseFloat(m.precio) > 0) return m;
            const hit = inv.find(it => it.nombre.toLowerCase() === (m.art || '').toLowerCase());
            return hit && parseFloat(hit.precio) > 0 ? { ...m, precio: String(hit.precio) } : m;
          }),
        }));
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function fld(k) { return { value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value }) )} }

  // Canvas firma
  function getPos(e, canvas) {
    const r = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (canvas.width / r.width), y: (src.clientY - r.top) * (canvas.height / r.height) };
  }
  function startDraw(e) {
    e.preventDefault();
    const cv = canvasRef.current; if (!cv) return;
    drawing.current = true;
    lastPos.current = getPos(e, cv);
  }
  function moveDraw(e) {
    e.preventDefault();
    if (!drawing.current) return;
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    const pos = getPos(e, cv);
    ctx.strokeStyle = '#1e2340';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }
  function endDraw() { drawing.current = false; }
  function clearSig() {
    const cv = canvasRef.current; if (!cv) return;
    cv.getContext('2d').clearRect(0, 0, cv.width, cv.height);
    setForm(p => ({ ...p, admin_firma: null }));
  }
  function isCanvasBlank() {
    const cv = canvasRef.current; if (!cv) return true;
    const blank = document.createElement('canvas');
    blank.width = cv.width; blank.height = cv.height;
    return cv.toDataURL() === blank.toDataURL();
  }
  function captureSig() {
    const cv = canvasRef.current; if (!cv) return null;
    return cv.toDataURL('image/png');
  }

  async function handleSave() {
    setSaving(true);
    try {
      let newData;
      if (isAdmin) {
        const canvasEmpty = isCanvasBlank();
        const firma = canvasEmpty ? null : captureSig();
        const isFirstSign = !pd.admin_firma && firma;
        newData = { ...pd, ...form, admin_firma: firma || form.admin_firma || null };
        await api.put(`/api/documents/${doc.id}/parte`, { parte_data: newData });

        // Descontar del inventario al firmar por primera vez
        if (isFirstSign && form.mats && form.mats.length > 0) {
          const ref = `Parte #${doc.id}${newData.nombre ? ' – ' + newData.nombre : ''}`;
          for (const m of form.mats) {
            const qty = parseFloat(m.qty) || 0;
            if (!qty || !m.art) continue;
            const hit = inventory.find(it => it.nombre.toLowerCase() === (m.art || '').toLowerCase());
            if (hit) {
              await api.post('/api/inventario/movimientos', {
                tipo: 'instalacion',
                material_id: hit.id,
                cantidad: qty,
                referencia: ref,
                notas: 'Descontado al firmar parte de trabajo',
              }).catch(() => {});
            }
          }
        }
      } else {
        newData = { ...pd, ...form };
        await api.put(`/api/documents/${doc.id}/parte`, { parte_data: newData });
      }

      // Regenerar el PDF descargable con los datos corregidos (best-effort:
      // si falla, el registro ya quedo guardado con los datos nuevos igualmente)
      const fileB64 = await regenPdf(newData);
      if (fileB64) {
        await api.patch(`/api/documents/${doc.id}/file`, { file_b64: fileB64 }).catch(() => {});
      }

      onSaved({ id: doc.id, parte_data: newData });
    } catch (e) { alert('Error al guardar: ' + e.message); }
    finally { setSaving(false); }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#10131e', border: '1px solid #1c2035', borderRadius: 14, padding: 24, maxWidth: 640, width: '100%', marginTop: 20, marginBottom: 20 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--amber)', marginBottom: 2 }}>Parte de trabajo</div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
              {doc.user_nombre && <span style={{ color: '#a78bfa', fontWeight: 700 }}>{doc.user_nombre} · </span>}
              {new Date(doc.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        <iframe
          ref={regenIframeRef}
          src="/partes.html"
          onLoad={() => setRegenReady(true)}
          title="pdf-worker"
          style={{ position: 'fixed', left: -9999, top: 0, width: 900, height: 1200, border: 'none', opacity: 0, pointerEvents: 'none' }}
        />

        {/* Tipo */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {['av', 'in', 'mn'].map(t => (
            <button key={t} onClick={() => setForm(p => ({ ...p, tipo: t }))} style={{
              padding: '6px 12px', borderRadius: 6, fontWeight: 700, fontSize: 11, cursor: 'pointer', border: 'none',
              background: form.tipo === t ? (TIPO_COLORS[t] + '33') : 'var(--surface2)',
              color: form.tipo === t ? TIPO_COLORS[t] : 'var(--text-dim)',
              outline: form.tipo === t ? `1.5px solid ${TIPO_COLORS[t]}` : 'none',
            }}>{TIPO_LABELS[t]}</button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={LBL_S}>Nº instalación</label>
            <input type="text" {...fld('numero')} style={INP_S} />
          </div>
          <div>
            <label style={LBL_S}>Fecha</label>
            <input type="text" {...fld('fecha')} placeholder="dd/mm/aaaa" style={INP_S} />
          </div>
        </div>
        <div style={{ marginBottom: 12, position: 'relative' }}>
          <label style={LBL_S}>Cliente / Instalación</label>
          <input type="text" value={form.nombre} style={INP_S}
            onChange={e => {
              const v = e.target.value;
              setForm(p => ({ ...p, nombre: v }));
              const vn = norm(v);
              if (vn.length < 2) { setInstSuggest([]); return; }
              const words = vn.split(/\s+/).filter(Boolean);
              const seen = new Set(); const hits = [];
              for (const inst of installations) {
                const hay = norm(instNombre(inst));
                if (!hay || seen.has(hay)) continue;
                if (words.every(w => hay.indexOf(w) >= 0)) { seen.add(hay); hits.push(inst); if (hits.length >= 8) break; }
              }
              setInstSuggest(hits);
              // Si el texto escrito ya apunta a una única dirección (aunque
              // no esté completo), autorrellenar directamente sin esperar a
              // que se elija del desplegable. Si coincide con varias
              // direcciones distintas no se adivina.
              if (hits.length === 1 && hits[0].provincia) {
                setForm(p => ({ ...p, provincia: hits[0].provincia.toUpperCase() }));
              }
            }}
            onBlur={() => setTimeout(() => setInstSuggest([]), 150)}
          />
          {instSuggest.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 2, background: '#10131e', border: '1px solid var(--border)', borderRadius: 7, maxHeight: 220, overflowY: 'auto' }}>
              {instSuggest.map((inst, i) => (
                <div key={i}
                  onMouseDown={() => {
                    const label = instNombre(inst);
                    setForm(p => ({ ...p, nombre: label, provincia: (inst.provincia || '').toUpperCase() }));
                    setInstSuggest([]);
                  }}
                  style={{ padding: '7px 10px', fontSize: 12, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 8, borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                  <span>{instNombre(inst)}</span>
                  <span style={{ color: 'var(--text-dim)', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{(inst.provincia || '').toUpperCase()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={LBL_S}>Provincia</label>
          <input type="text" value={form.provincia}
            onChange={e => setForm(p => ({ ...p, provincia: e.target.value.toUpperCase() }))}
            style={INP_S} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <label style={{ ...LBL_S, marginBottom: 0 }}>Técnicos</label>
            <button type="button" onClick={() => setForm(p => ({ ...p, tecs: [...(p.tecs && p.tecs.length ? p.tecs : []), ''] }))}
              style={{ background: 'none', border: 'none', color: 'var(--amber)', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
              + Añadir técnico
            </button>
          </div>
          {(form.tecs && form.tecs.length ? form.tecs : ['']).map((t, i) => (
            <div key={i} style={{ position: 'relative', display: 'flex', gap: 7, marginBottom: 6 }}>
              <input type="text" value={t} placeholder="Nombre Apellido"
                onChange={e => {
                  const v = e.target.value;
                  setForm(p => { const tecs = [...(p.tecs && p.tecs.length ? p.tecs : [''])]; tecs[i] = v; return { ...p, tecs }; });
                  const vn = norm(v);
                  if (!vn) { setTecSuggestIdx(null); return; }
                  setTecSuggest(employees.filter(e => norm(e.nombre).indexOf(vn) >= 0).slice(0, 8));
                  setTecSuggestIdx(i);
                }}
                onBlur={() => setTimeout(() => setTecSuggestIdx(null), 150)}
                style={{ ...INP_S, flex: 1 }} />
              <button type="button"
                onClick={() => setForm(p => { const tecs = (p.tecs || []).filter((_, j) => j !== i); return { ...p, tecs: tecs.length ? tecs : [''] }; })}
                style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
              {tecSuggestIdx === i && tecSuggest.length > 0 && (
                <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 28, marginTop: 2, background: '#10131e', border: '1px solid var(--border)', borderRadius: 7, maxHeight: 180, overflowY: 'auto' }}>
                  {tecSuggest.map((emp, j) => (
                    <div key={j}
                      onMouseDown={() => {
                        setForm(p => { const tecs = [...(p.tecs && p.tecs.length ? p.tecs : [''])]; tecs[i] = emp.nombre; return { ...p, tecs }; });
                        setTecSuggestIdx(null);
                      }}
                      style={{ padding: '7px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                      {emp.nombre}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={LBL_S}>Objeto del trabajo</label>
          <input type="text" {...fld('objeto')} style={INP_S} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={LBL_S}>Descripción de trabajos realizados</label>
          <textarea rows={4} {...fld('trabajos')} style={{ ...INP_S, resize: 'vertical' }} />
        </div>

        {/* Materiales */}
        {form.mats && form.mats.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <label style={LBL_S}>Materiales utilizados</label>
              {!pd.admin_firma && inventory.length > 0 && (
                <span style={{ fontSize: 9, color: '#4ade80', fontWeight: 700 }}>
                  ↓ Al firmar se descontará del inventario
                </span>
              )}
            </div>
            <div style={{ background: 'var(--surface2)', borderRadius: 7, padding: '10px 12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '56px 1fr 76px 20px auto', gap: 7, marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
                {['Cant.', 'Artículo', 'Precio €', '', ''].map((h, i) => (
                  <span key={i} style={{ fontSize: 9, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{h}</span>
                ))}
              </div>
              {form.mats.map((m, i) => {
                const inInv = inventory.length > 0 && inventory.some(it => it.nombre.toLowerCase() === (m.art || '').toLowerCase());
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '56px 1fr 76px 20px auto', gap: 7, alignItems: 'center', marginBottom: 5 }}>
                    <input type="text" value={m.qty} onChange={e => setForm(p => { const mats = [...p.mats]; mats[i] = { ...mats[i], qty: e.target.value }; return { ...p, mats }; })}
                      style={INP_S} placeholder="qty" />
                    <input type="text" value={m.art} onChange={e => setForm(p => { const mats = [...p.mats]; mats[i] = { ...mats[i], art: e.target.value }; return { ...p, mats }; })}
                      style={INP_S} placeholder="artículo" />
                    <input type="number" min="0" step="0.01" value={m.precio || ''} onChange={e => setForm(p => { const mats = [...p.mats]; mats[i] = { ...mats[i], precio: e.target.value }; return { ...p, mats }; })}
                      style={{ ...INP_S, textAlign: 'right' }} placeholder="0.00" />
                    <span title={inInv ? 'En inventario — se descontará al firmar' : 'No encontrado en inventario'}
                      style={{ fontSize: 11, textAlign: 'center', opacity: inventory.length === 0 ? 0.3 : 1 }}>
                      {inInv ? '✔' : '—'}
                    </span>
                    <button onClick={() => setForm(p => ({ ...p, mats: p.mats.filter((_, j) => j !== i) }))}
                      style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
                  </div>
                );
              })}
              {(() => {
                const total = form.mats.reduce((acc, m) => acc + (parseFloat(m.qty) || 0) * (parseFloat(m.precio) || 0), 0);
                return total > 0 ? (
                  <div style={{ textAlign: 'right', fontSize: 11, fontWeight: 800, color: 'var(--amber)', marginTop: 6, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
                    Total: {total.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        )}

        {/* Notas admin */}
        {isAdmin && (
          <div style={{ marginBottom: 16 }}>
            <label style={LBL_S}>Notas del responsable (visible solo internamente)</label>
            <textarea rows={2} {...fld('admin_notas')} style={{ ...INP_S, resize: 'vertical' }} placeholder="Observaciones del administrativo…" />
          </div>
        )}

        {/* Firma admin */}
        {isAdmin && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={LBL_S}>Firma responsable</label>
              <button onClick={clearSig} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 11 }}>Limpiar</button>
            </div>
            {form.admin_firma && !canvasRef.current?.getContext('2d').isPointInPath ? (
              <img src={form.admin_firma} alt="firma" style={{ width: '100%', height: 100, objectFit: 'contain', background: '#f8f9fa', borderRadius: 7, border: '1px solid var(--border)' }} />
            ) : null}
            <canvas
              ref={canvasRef}
              width={580} height={120}
              onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
              onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
              style={{ display: 'block', width: '100%', height: 120, background: '#f8f9fa', borderRadius: 7, border: '1px solid var(--border)', cursor: 'crosshair', touchAction: 'none' }}
            />
            <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>Firma encima del área blanca</div>
          </div>
        )}
        {!isAdmin && (
          <div style={{ marginBottom: 16, fontSize: 11, color: 'var(--text-dim)' }}>
            Puedes corregir tus datos mientras el parte no haya sido firmado por el responsable.
            Al guardar se regenera el PDF descargable con los cambios; la firma manuscrita original
            no se puede reproducir en el PDF regenerado, así que se mostrará tu nombre en su lugar.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'var(--surface2)', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '10px', background: 'var(--amber)', color: '#000', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Guardando…' : !isAdmin ? '💾 Guardar cambios' : pd.admin_firma ? '💾 Guardar cambios' : '✔ Firmar y descontar inventario'}
          </button>
        </div>
      </div>
    </div>
  );
}

const LBL_S = { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 3 };
const INP_S = { background: '#070910', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '7px 10px', width: '100%', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' };
